// src/utils/taskOrderingUtils.js
// Unified task ordering utilities to ensure consistent sorting across all views

/**
 * Bulletproof task ordering function
 * This function ensures that tasks are always sorted consistently across Kanban, List, and all other views
 * 
 * @param {Array} tasks - Array of task objects
 * @param {string|null} sectionId - Section ID to filter by (null for unsectioned tasks)
 * @returns {Array} Sorted tasks
 */
export const sortTasksByOrder = (tasks, sectionId = undefined) => {
  if (!Array.isArray(tasks)) {
    console.warn('sortTasksByOrder: tasks is not an array', tasks);
    return [];
  }

  // Filter by section if specified
  let filteredTasks = tasks;
  if (sectionId !== undefined) {
    filteredTasks = tasks.filter(task => {
      if (sectionId === null) {
        // Looking for unsectioned tasks
        return !task.sectionId;
      }
      // Looking for tasks in specific section
      return task.sectionId && task.sectionId.toString() === sectionId.toString();
    });
  }

  // Sort by orderIndex with fallback for tasks without orderIndex
  return filteredTasks.sort((a, b) => {
    const orderA = a.orderIndex ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.orderIndex ?? Number.MAX_SAFE_INTEGER;
    
    if (orderA === orderB) {
      // If orderIndex is the same, sort by creation date as fallback
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateA - dateB;
    }
    
    return orderA - orderB;
  });
};

/**
 * Get tasks for a specific section with consistent ordering
 * This is the exact same logic used in useTaskViewBase
 * 
 * @param {Array} tasks - All tasks
 * @param {string|null} sectionId - Section ID (null for unsectioned)
 * @returns {Array} Sorted tasks for the section
 */
export const getTasksForSection = (tasks, sectionId) => {
  return sortTasksByOrder(tasks, sectionId);
};

/**
 * Validate task ordering within sections
 * This function can be used to detect ordering inconsistencies
 * 
 * @param {Array} tasks - Array of tasks
 * @returns {Object} Validation result with any issues found
 */
export const validateTaskOrdering = (tasks) => {
  const issues = [];
  const sectionGroups = {};

  // Group tasks by section
  tasks.forEach(task => {
    const sectionKey = task.sectionId || 'unsectioned';
    if (!sectionGroups[sectionKey]) {
      sectionGroups[sectionKey] = [];
    }
    sectionGroups[sectionKey].push(task);
  });

  // Check each section for ordering issues
  Object.entries(sectionGroups).forEach(([sectionKey, sectionTasks]) => {
    const sortedTasks = sortTasksByOrder(sectionTasks);
    const orderIndices = sortedTasks.map(t => t.orderIndex);
    
    // Check for gaps or duplicates
    const uniqueIndices = [...new Set(orderIndices)];
    if (uniqueIndices.length !== orderIndices.length) {
      issues.push({
        section: sectionKey,
        type: 'duplicate_indices',
        tasks: sectionTasks.filter((task, index) => 
          orderIndices.indexOf(task.orderIndex) !== index
        )
      });
    }

    // Check for expected sequential ordering
    const expectedIndices = Array.from({ length: sectionTasks.length }, (_, i) => i);
    const actualIndices = orderIndices.filter(i => i !== undefined && i !== null);
    
    if (JSON.stringify(expectedIndices) !== JSON.stringify(actualIndices)) {
      issues.push({
        section: sectionKey,
        type: 'non_sequential',
        expected: expectedIndices,
        actual: actualIndices
      });
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
    summary: `Found ${issues.length} ordering issues across ${Object.keys(sectionGroups).length} sections`
  };
};

/**
 * Debug function to log task ordering information
 * Use this to trace ordering issues during development
 * 
 * @param {Array} tasks - Tasks to debug
 * @param {string} context - Context description for logging
 */
export const debugTaskOrdering = (tasks, context = 'Unknown') => {
  console.group(`🔍 Task Ordering Debug: ${context}`);
  
  const validation = validateTaskOrdering(tasks);
  console.log('Validation Result:', validation);
  
  // Group by section for detailed logging
  const sectionGroups = {};
  tasks.forEach(task => {
    const sectionKey = task.sectionId || 'unsectioned';
    if (!sectionGroups[sectionKey]) {
      sectionGroups[sectionKey] = [];
    }
    sectionGroups[sectionKey].push(task);
  });

  Object.entries(sectionGroups).forEach(([sectionKey, sectionTasks]) => {
    console.group(`📁 Section: ${sectionKey}`);
    const sorted = sortTasksByOrder(sectionTasks);
    sorted.forEach((task, index) => {
      console.log(`${index}: ${task.title} (orderIndex: ${task.orderIndex}, id: ${task._id})`);
    });
    console.groupEnd();
  });
  
  console.groupEnd();
  
  return validation;
};

/**
 * Ensure tasks have valid sequential orderIndex values
 * This can be used to fix ordering issues on the client side
 * 
 * @param {Array} tasks - Tasks to fix
 * @returns {Array} Tasks with corrected orderIndex values
 */
export const normalizeTaskOrdering = (tasks) => {
  const sectionGroups = {};
  
  // Group tasks by section
  tasks.forEach(task => {
    const sectionKey = task.sectionId || 'unsectioned';
    if (!sectionGroups[sectionKey]) {
      sectionGroups[sectionKey] = [];
    }
    sectionGroups[sectionKey].push(task);
  });

  // Normalize ordering within each section
  const normalizedTasks = [];
  
  Object.values(sectionGroups).forEach(sectionTasks => {
    const sortedTasks = sortTasksByOrder(sectionTasks);
    sortedTasks.forEach((task, index) => {
      normalizedTasks.push({
        ...task,
        orderIndex: index
      });
    });
  });

  return normalizedTasks;
};

// Export default object with all utilities
export default {
  sortTasksByOrder,
  getTasksForSection,
  validateTaskOrdering,
  debugTaskOrdering,
  normalizeTaskOrdering
};